import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpRightIcon } from '@heroicons/react/24/outline';

import { DealResponseDto, FighterDashboardStatsDto, PaymentResponseDto } from '../../types/types';
import { extractCurrency } from '../../utils/deals/dealHelpers';
import { getCurrencySymbol } from '../../utils/opportunities/opportunityAndReadyRoster';
import { EmptyState } from './emptyState';
import { TableRowsSkeleton } from './skeleton';

export type DealTab = 'active' | 'inactive';

interface DealsSectionProps {
  stats: FighterDashboardStatsDto | null;
  isFighter: boolean;
  isManager: boolean;
  payments: PaymentResponseDto[];
  activeDeals: DealResponseDto[];
  activeDealsLoading: boolean;
  dealTab: DealTab;
  onDealTabChange: (tab: DealTab) => void;
}

interface DealRow {
  key: string;
  deal: DealResponseDto;
  eventDate: string;
  expense: string;
  income: string;
  currency: string;
  paidAt: string;
  counterpartyName: string;
}

interface TableColumn {
  header: string;
  size: number;
  show: boolean;
  render: (row: DealRow) => React.ReactNode;
}

const PAID_PAYMENT_STATUSES = ['PAID', 'COMPLETED'];

const STATUS_BADGE_COLORS: Record<string, string> = {
  ACCEPTED: 'bg-orange-200',
  CONFIRMED: 'bg-green-200',
  WAITING_FOR_RESULT: 'bg-yellow-200',
  PROPOSED: 'bg-blue-200',
  RESULT_CONFIRMED: 'bg-purple-200',
  RESULT_UPDATED: 'bg-amber-200',
};

const getStatusBadgeColor = (status?: string | null): string => {
  const statusKey = String(status || '').toUpperCase();

  if (statusKey.includes('RATED')) return 'bg-teal-200';

  return STATUS_BADGE_COLORS[statusKey] || 'bg-gray-200';
};

const getDealCurrency = (deal: DealResponseDto, payment?: PaymentResponseDto): string =>
  extractCurrency(deal) || (payment as any)?.sentCurrency || '—';

const getDealEventDate = (deal: DealResponseDto): string =>
  deal?.formData?.details?.eventDate ?? deal?.formData?.eventDate ?? '';

const formatAmount = (value: unknown): string => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? '—' : parsedDate.toLocaleDateString();
};

const formatCodeLabel = (value?: string | null): string =>
  String(value || '—')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const cleanId = (value?: string | number | null): string => {
  if (value === null || value === undefined) return '';

  const text = String(value).trim();
  const lowerText = text.toLowerCase();

  if (!text || lowerText === 'nan' || lowerText === 'undefined' || lowerText === 'null') {
    return '';
  }

  return text;
};

const getDealPageId = (deal: DealResponseDto): string =>
  cleanId(deal.id) || cleanId(deal.dealId);

const getShownDealId = (deal: DealResponseDto): string =>
  cleanId(deal.dealId) || cleanId(deal.id) || '-';

const getPaidInfo = (deal: DealResponseDto): { total: number; lastPaidOn: string } => {
  const componentPayments: any[] = (deal as any).componentPayments ?? [];
  const paidPayments = componentPayments.filter((payment) =>
    PAID_PAYMENT_STATUSES.includes(String(payment.status || '').toUpperCase())
  );

  const total = paidPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const lastPaidOn =
    paidPayments
      .map((payment) => payment.paidOn)
      .filter(Boolean)
      .sort()
      .pop() || '';

  return { total, lastPaidOn };
};

interface Trend {
  percent: number;
  increased: boolean;
}

const TrendLine: React.FC<{ trend: Trend }> = ({ trend }) => {
  const { t } = useTranslation('dashboard');
  const Icon = trend.increased ? ArrowUpIcon : ArrowDownIcon;

  return (
    <p className="flex items-center gap-2 text-sm leading-5 text-gray-900">
      <span
        className={`flex items-center gap-1 font-medium ${
          trend.increased ? 'text-green-500' : 'text-red-500'
        }`}
      >
        <Icon className="h-4 w-4" />
        {trend.percent}%
      </span>
      {t('from_last_month')}
    </p>
  );
};

const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  trend?: Trend;
  isEmpty: boolean;
  emptyText: string;
}> = ({ title, value, trend, isEmpty, emptyText }) => (
  <div className="flex min-w-0 flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
    <div className="flex flex-col gap-4">
      <p className="text-sm font-medium leading-5 text-gray-900">{title}</p>
      {isEmpty ? (
        <EmptyState text={emptyText} iconClassName="h-12 w-12" />
      ) : (
        <>
          <p className="truncate text-4xl font-extrabold leading-10 text-gray-900">{value}</p>
          {trend && <TrendLine trend={trend} />}
        </>
      )}
    </div>
  </div>
);

const StatusBadge: React.FC<{ status?: string | null; label: string }> = ({ status, label }) => (
  <span
    className={`inline-flex h-5 w-fit max-w-full items-center rounded-xl px-1 py-0.5 text-[12px] font-medium leading-[16px] text-gray-900 ${getStatusBadgeColor(
      status
    )}`}
  >
    <span className="truncate">{label}</span>
  </span>
);

const DealsTable: React.FC<{
  columns: TableColumn[];
  rows: DealRow[];
  isManager: boolean;
  isFighter: boolean;
  onOpenDeal: (deal: DealResponseDto) => void;
  getDealTypeLabel: (dealType?: string | null) => string;
  getDealStatusLabel: (status?: string | null) => string;
}> = ({
  columns,
  rows,
  isManager,
  isFighter,
  onOpenDeal,
  getDealTypeLabel,
  getDealStatusLabel,
}) => {
  const gridColumns = columns.map((column) => `minmax(0, ${column.size}fr)`).join(' ');
  const minWidth = columns.reduce((sum, column) => sum + column.size, 0) + columns.length * 16 + 40;

  return (
    <>
      {/* Mobile Card View (md:hidden) */}
      <div className="flex flex-col gap-3 md:hidden max-h-[900px] overflow-y-auto [scrollbar-width:thin]">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-col rounded-2xl bg-white border border-gray-100 shadow-xs overflow-hidden"
          >
            {/* Header: Deal ID + Status */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex flex-col">
                <span className="text-[12px] font-medium leading-[16px] text-gray-500">
                  Deal ID
                </span>
                <button
                  type="button"
                  onClick={() => onOpenDeal(row.deal)}
                  className="text-left text-base font-bold leading-6 text-gray-900 hover:underline"
                >
                  {getShownDealId(row.deal)}
                </button>
              </div>
              <StatusBadge
                status={row.deal.status}
                label={getDealStatusLabel(row.deal.status)}
              />
            </div>

            {/* Row: Deal type */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
              <span className="text-xs font-medium leading-4 text-gray-500">
                Deal type
              </span>
              <span className="text-right text-sm font-medium leading-5 text-gray-900">
                {getDealTypeLabel(row.deal.dealType)}
              </span>
            </div>

            {/* Row: Event date */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
              <span className="text-xs font-medium leading-4 text-gray-500">
                Event date
              </span>
              <span className="text-right text-sm font-medium leading-5 text-gray-900">
                {row.eventDate}
              </span>
            </div>

            {/* Row: Expense amount (if manager) */}
            {isManager && (
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                <span className="text-xs font-medium leading-4 text-gray-500">
                  Expense amount
                </span>
                <span className="text-right text-sm font-normal leading-5 text-red-500">
                  {row.expense}
                </span>
              </div>
            )}

            {/* Row: Income / Refund */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
              <span className="text-xs font-medium leading-4 text-gray-500">
                {isFighter ? 'Income' : 'Refund'}
              </span>
              <span className="text-right text-sm font-normal leading-5 text-amber-500">
                {row.income}
              </span>
            </div>

            {/* Row: Currency */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
              <span className="text-xs font-medium leading-4 text-gray-500">
                Currency
              </span>
              <span className="text-right text-sm font-normal leading-5 text-gray-900">
                {row.currency}
              </span>
            </div>

            {/* Row: Paid at */}
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs font-medium leading-4 text-gray-500">
                Paid at
              </span>
              <span className="text-right text-sm font-medium leading-5 text-gray-900">
                {row.paidAt}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View (hidden md:block) */}
      <div
        className="hidden md:block max-h-[604px] overflow-auto rounded-[20px] bg-gray-50 [scrollbar-width:thin]"
      >
        <div style={{ minWidth }} className="px-1 pb-1">
          <div
            style={{ gridTemplateColumns: gridColumns }}
            className="sticky top-0 z-10 grid gap-4 bg-gray-50 px-4 py-2.5 text-left text-[12px] font-medium leading-[16px] text-gray-500"
          >
            {columns.map((column) => (
              <span key={column.header} className="truncate">
                {column.header}
              </span>
            ))}
          </div>

          <div className="rounded-2xl bg-white">
            {rows.map((row) => (
              <div
                key={row.key}
                style={{ gridTemplateColumns: gridColumns }}
                className="grid h-14 items-center gap-4 border-b border-gray-100 px-4 py-3 text-left text-[14px] font-normal leading-[20px] text-gray-900 last:border-b-0"
              >
                {columns.map((column) => (
                  <React.Fragment key={column.header}>{column.render(row)}</React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const messageClass = 'py-8 text-center text-[14px] leading-[20px] text-gray-500';

export const DealsSummaryCards: React.FC<{
  stats: FighterDashboardStatsDto | null;
}> = ({ stats }) => {
  const { t } = useTranslation('dashboard');
  const isEmpty = !stats || (stats.numberOfDeals === 0 && stats.dealsChangePercent === 0);
  const emptyText = t('no_data_to_display');
  const currencySymbol = getCurrencySymbol(stats?.currency ?? '');
  // Backend may omit totalExpenses (no expense tracking yet) - show it as 0 like the money-flow API does
  const totalExpenses = stats?.totalExpenses ?? 0;
  const hasExpenseTrend = stats?.totalExpenses != null;

  return (
    <div className="flex flex-col gap-6 sm:grid sm:grid-cols-3 font-['Plus_Jakarta_Sans',sans-serif]">
      <SummaryCard
        title={t('total_income')}
        value={`${formatAmount(stats?.totalIncome)} ${currencySymbol}`}
        trend={
          stats
            ? { percent: stats.incomeChangePercent, increased: stats.incomeIncreased }
            : undefined
        }
        isEmpty={isEmpty}
        emptyText={emptyText}
      />
      <SummaryCard
        title={t('total_expense')}
        value={`${formatAmount(totalExpenses)} ${currencySymbol}`}
        trend={
          stats && hasExpenseTrend
            ? { percent: stats.expensesChangePercent, increased: stats.expensesIncreased }
            : undefined
        }
        isEmpty={isEmpty}
        emptyText={emptyText}
      />
      <SummaryCard
        title={t('total_deals')}
        value={stats?.numberOfDeals ?? 0}
        trend={
          stats
            ? { percent: stats.dealsChangePercent, increased: stats.dealsIncreased }
            : undefined
        }
        isEmpty={isEmpty}
        emptyText={emptyText}
      />
    </div>
  );
};

export const DealsListCard: React.FC<Omit<DealsSectionProps, 'stats'>> = ({
  isFighter,
  isManager,
  payments,
  activeDeals,
  activeDealsLoading,
  dealTab,
  onDealTabChange,
}) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const showIncomeColumn = isFighter || isManager;

  const getTranslatedText = (key: string, fallback: string): string => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const getDealTypeLabel = (dealType?: string | null): string =>
    getTranslatedText(`deal_types.${dealType}`, formatCodeLabel(dealType));

  const getDealStatusLabel = (status?: string | null): string =>
    getTranslatedText(`deal_status.${status}`, formatCodeLabel(status));

  const openDealPage = (deal: DealResponseDto) => {
    const pageId = getDealPageId(deal);

    if (!pageId) {
      toast.error('Deal ID not available');
      return;
    }

    navigate(`/deal/view/${pageId}`);
  };

  const getTabClass = (isSelected: boolean): string =>
    `rounded-3xl px-3 py-1.5 text-sm font-medium leading-5 transition ${
      isSelected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
    }`;

  const getColumns = (showCounterparty: boolean): TableColumn[] =>
    [
      {
        header: t('deal_id'),
        size: 160,
        show: true,
        render: (row: DealRow) => (
          <button
            type="button"
            onClick={() => openDealPage(row.deal)}
            className="truncate text-left font-medium text-gray-900 hover:underline"
          >
            {getShownDealId(row.deal)}
          </button>
        ),
      },
      {
        header: t('deal_type'),
        size: 128,
        show: true,
        render: (row: DealRow) => (
          <span className="truncate">{getDealTypeLabel(row.deal.dealType)}</span>
        ),
      },
      {
        header: isManager ? t('fighter') : t('manager'),
        size: 144,
        show: showCounterparty,
        render: (row: DealRow) => <span className="truncate">{row.counterpartyName}</span>,
      },
      {
        header: t('event_date'),
        size: 128,
        show: true,
        render: (row: DealRow) => <span className="truncate">{row.eventDate}</span>,
      },
      {
        header: t('expense_amount'),
        size: 112,
        show: isManager,
        render: (row: DealRow) => <span className="truncate text-red-500">{row.expense}</span>,
      },
      {
        header: isFighter ? t('income') : t('refund'),
        size: 112,
        show: showIncomeColumn,
        render: (row: DealRow) => <span className="truncate text-amber-500">{row.income}</span>,
      },
      {
        header: t('currency'),
        size: 80,
        show: true,
        render: (row: DealRow) => <span className="truncate">{row.currency}</span>,
      },
      {
        header: t('status'),
        size: 192,
        show: true,
        render: (row: DealRow) => (
          <StatusBadge status={row.deal.status} label={getDealStatusLabel(row.deal.status)} />
        ),
      },
      {
        header: t('paid_at'),
        size: 128,
        show: true,
        render: (row: DealRow) => <span className="truncate">{row.paidAt}</span>,
      },
    ].filter((column) => column.show);

  const renderActiveTable = () => {
    if (activeDealsLoading) {
      return <TableRowsSkeleton />;
    }

    if (activeDeals.length === 0) {
      return <EmptyState text={t('no_deals_to_display')} />;
    }

    const rows: DealRow[] = activeDeals.map((deal, index) => {
      const otherPerson = isManager ? deal.acceptedBy : deal.initiatedBy;
      const paidInfo = getPaidInfo(deal);
      const paidAmount = formatAmount(paidInfo.total);

      return {
        key: getDealPageId(deal) || `active-deal-${index}`,
        deal,
        eventDate: formatDate(getDealEventDate(deal)),
        expense: isManager ? paidAmount : '—',
        income: isFighter ? paidAmount : '—',
        currency: getDealCurrency(deal),
        paidAt: formatDate(paidInfo.lastPaidOn),
        counterpartyName: otherPerson ? `${otherPerson.firstName} ${otherPerson.lastName}` : '—',
      };
    });

    return (
      <DealsTable
        columns={getColumns(true)}
        rows={rows}
        isManager={isManager}
        isFighter={isFighter}
        onOpenDeal={openDealPage}
        getDealTypeLabel={getDealTypeLabel}
        getDealStatusLabel={getDealStatusLabel}
      />
    );
  };

  const renderClosedTable = () => {
    if (payments.length === 0) {
      return <EmptyState text={t('no_deals_to_display')} />;
    }

    const rows: DealRow[] = payments.map((payment, index) => ({
      key: getDealPageId(payment.deal) || `closed-deal-${index}`,
      deal: payment.deal,
      eventDate: formatDate(getDealEventDate(payment.deal)),
      expense: formatAmount(payment.sentAmount),
      income: formatAmount(payment.receivedAmount),
      currency: getDealCurrency(payment.deal, payment),
      paidAt: formatDate(payment.paidAt),
      counterpartyName: '',
    }));

    return (
      <DealsTable
        columns={getColumns(false)}
        rows={rows}
        isManager={isManager}
        isFighter={isFighter}
        onOpenDeal={openDealPage}
        getDealTypeLabel={getDealTypeLabel}
        getDealStatusLabel={getDealStatusLabel}
      />
    );
  };

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <p className="text-left text-sm font-medium leading-5 text-gray-900">
        {t('deals_title', { defaultValue: 'Deals' })}
      </p>

      <div className="flex flex-col gap-4">
        <div className="inline-flex w-fit gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
          <button
            type="button"
            onClick={() => onDealTabChange('active')}
            className={getTabClass(dealTab === 'active')}
          >
            {t('active_deal')}
          </button>
          <button
            type="button"
            onClick={() => onDealTabChange('inactive')}
            className={getTabClass(dealTab === 'inactive')}
          >
            {t('closed_deal')}
          </button>
        </div>

        {dealTab === 'active' ? renderActiveTable() : renderClosedTable()}
      </div>

      <button
        type="button"
        onClick={() => navigate('/mydeals')}
        className="flex w-fit items-center gap-0.5 text-sm font-medium leading-5 text-blue-600 hover:text-blue-700"
      >
        {t('go_to_deals')}
        <ArrowUpRightIcon className="h-2.5 w-2.5 opacity-60" />
      </button>
    </section>
  );
};

export const DealsSection: React.FC<DealsSectionProps> = ({ stats, ...rest }) => {
  return (
    <div className="space-y-6 font-['Plus_Jakarta_Sans',sans-serif]">
      <DealsSummaryCards stats={stats} />
      <DealsListCard {...rest} />
    </div>
  );
};